import { Typography } from '@material-ui/core';
import { get as _get } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useRouteMatch } from 'react-router-dom';
import { CARGO_TYPE } from '../../../cargoType';
import { ListPageWithPersisted, StaticListPage } from '../../../layout/ListPage';
import { ListPageHeader } from '../../../layout/ListPage/headers';
import LoadingIndicator from '../../../layout/loading/LoadingIndicator';
import columnsSetting from '../../../layout/Datagrid/ColumnSetting';
import { formatNumberWithAccounting } from '../../../common/helpers/numbers';
import { MassEnum } from '@vooban/tc3-core';
import ItemMovementListHelper from '../itemMovement/itemMovementHelpers';
import { InventoryItem } from '../types/InventoryItem';
import { useGetItemMovements } from './useGetItem';

// buffer of 30 secs to include those that were executed during the same operation
const OPERATION_TIME_BUFFER_SECONDS = 30;

const getPreviousMovementProps = (movement) => {
  const getProps = (object, prefix = 'Destination') => {
    const prefixLowerCase = prefix.toLocaleLowerCase();
    return {
      order: _get(object, `${prefixLowerCase}Order`),
      locationName: _get(object, `${prefixLowerCase}LocationName`),
      inventoryId: _get(object, `${prefixLowerCase}Inventory.id`),
      inventoryName: _get(object, `${prefixLowerCase}Inventory.name`),
      inventoryItemId: _get(object, `movement${prefix}.${prefixLowerCase}InventoryItemsIds.0`),
    };
  };
  return getProps(movement, 'Destination');
};

const getPreviousMovementsHeader = ([prevItemMovement], t) => {
  const movementProps = getPreviousMovementProps(prevItemMovement);
  const { inventoryName, order, locationName } = movementProps;
  const { name: orderName = t('item.noOrder') } = order || {};
  return <span>{[inventoryName, orderName, locationName].filter(Boolean).join(' - ')}</span>;
};

const defaultDisplayWeightDecimalPlaces = 4;

const formatQuantity = (t, itemCargoType) => (totalQuantity) => {
  const isBreakBulk = itemCargoType === CARGO_TYPE.breakbulk;
  return isBreakBulk
    ? formatNumberWithAccounting(totalQuantity, 0)
    : `${formatNumberWithAccounting(totalQuantity, defaultDisplayWeightDecimalPlaces)} ${t(`units.${MassEnum.MT}`)}`;
};

const formatDescription = () => (value) => {
  const splitValue = value.toString().split(';');
  if (splitValue.length > 1) {
    return <button onClick={() => window.open(splitValue[1], '_blank')}>{splitValue[0]}</button>;
  }
  return <div dangerouslySetInnerHTML={{ __html: value.toString() }} />;
};

const ItemMovementsPage = ({ item }) => {
  const { inventoryId, itemId } = useParams();
  const [fetchItemMovements, itemMovementsData = {}, isLoading] = useGetItemMovements(inventoryId, itemId);
  const { t, i18n } = useTranslation('inventory');
  const { path } = useRouteMatch();

  const previousMovements = useMemo(() => {
    return (itemMovementsData.previousMovements || []).reduce((acc, movementList) => {
      const rawMovements = itemMovementsData.movements?.items || itemMovementsData.movements || [];
      const latestMovements = acc.length ? acc.flat() : rawMovements;

      const latestMovementDates = latestMovements.map((x) => x.effectiveDate);
      latestMovementDates.sort();
      const [firstDate] = latestMovementDates;

      return [...acc, movementList.filter((m) => moment(firstDate).diff(m.effectiveDate) > -OPERATION_TIME_BUFFER_SECONDS)];
    }, []);
  }, [itemMovementsData]);

  const getHasPreviousMovements = () => previousMovements.length > 0;

  const movements = useMemo(() => {
    const rawMovements = itemMovementsData.movements?.items || itemMovementsData.movements || [];
    if (!item || !rawMovements.length) return [];
    return item.cargoType === CARGO_TYPE.breakbulk
      ? ItemMovementListHelper.toBreakbulkList(rawMovements, t)
      : ItemMovementListHelper.toBulkList(rawMovements, t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, itemMovementsData, i18n.language]);

  const data = useMemo(() => {
    const movementsData = itemMovementsData.movements || {};
    return {
      items: movements,
      totalItems: movementsData.totalItems || movements.length,
      pageNumber: movementsData.pageNumber || 1,
    };
  }, [movements, itemMovementsData]);

  const columns = useMemo(() => {
    if (!item) return [];
    const effectiveDate = columnsSetting('effectiveDate').withFormatDate().withWidth(140);
    const movement = columnsSetting('movementDescription').withCustomFormat(formatDescription());
    const actionNumber = columnsSetting('actionNumber').withWidth(170);
    const movedQuantity = columnsSetting('movedQuantity').withCustomFormat(formatQuantity(t, item.cargoType)).withAlignRight().withWidth(170);
    const bookQuantity = columnsSetting('totalQuantity').withCustomFormat(formatQuantity(t, item.cargoType)).withAlignRight().withWidth(170);

    return [effectiveDate, movement, actionNumber, movedQuantity, bookQuantity].map((x) => x.withIsVisible().withoutSort());
  }, [item, t]);

  const getPrevMovements = (prevMovements) => {
    if (!item || !prevMovements.length) return [];
    return item.cargoType === CARGO_TYPE.breakbulk
      ? ItemMovementListHelper.toBreakbulkPreviousList(prevMovements, t)
      : ItemMovementListHelper.toBulkPreviousList(prevMovements, t);
  };

  return (
    <>
      <LoadingIndicator show={isLoading} />
      <div className="pb-4 flex justify-between">
        <h1 className="text-lg inline-block" data-testid="title_movements">
          {t('item.movements')}
        </h1>
      </div>
      {columns.length > 0 && (
        <ListPageWithPersisted
          persistedKey={path}
          header={ListPageHeader}
          fetcher={fetchItemMovements}
          data={data}
          columns={columns}
          translater={t}
          noRowLink
          headerProps={{}}
        />
      )}
      {getHasPreviousMovements() && item.cargoType === CARGO_TYPE.breakbulk && (
        <div className="rdg-hide-header">
          <div className="pb-3 pt-3 flex justify-between">
            <Typography style={{ textTransform: 'uppercase' }}>{t('item.previously')}</Typography>
          </div>
          {previousMovements
            .filter((x) => x.length > 0)
            .map((prevItemMovements, index) => {
              const prevMovements = getPrevMovements(prevItemMovements);
              const prevData = { items: prevMovements, totalItems: prevMovements.length };
              return (
                <div key={String(index)}>
                  <div className="pb-2">
                    <Typography>{getPreviousMovementsHeader(prevItemMovements, t)}</Typography>
                  </div>
                  <StaticListPage data={prevData} defaultColumns={columns} otherColumns={[]} translater={t} noRowLink />
                </div>
              );
            })}
        </div>
      )}
    </>
  );
};

ItemMovementsPage.propTypes = {
  item: PropTypes.shape(InventoryItem).isRequired,
};

export default ItemMovementsPage;
